<template>
    <FilterComponent :displayText="title" ref="filter" class="date-range-filter">
        <div class="selection-section" v-for="(section, i) in sections" :key="i">
            <mt-radio-button
                v-for="option in section"
                :key="option.display"
                :value="option"
                v-model="currentOption"
                :label="option.display"
                class="selection"
            />
            <hr />
        </div>
        <div class="selection-section">
            <div class="selection custom-date-selector">
                <div class="custom-date-selector-title">
                    <i class="material-icons">date_range</i>
                    <span>Custom Date</span>
                </div>
                <div class="boxes">
                    <input v-model="customDateStart" type="date" name="startRange" @focus="showDatePicker" />
                    <span class="to">To</span>
                    <input v-model="customDateEnd" type="date" name="endRange" @focus="showDatePicker" />
                </div>
                <CalendarInput
                    v-if="datePickerVisible"
                    :startDate.sync="customDateStart"
                    :endDate.sync="customDateEnd"
                />
                <div>
                    <button
                        class="set-date"
                        v-if="datePickerVisible"
                        :disabled="setDateButtonDisabled"
                        type="button"
                        @click="setDateClicked"
                    >
                        Set Date
                    </button>
                </div>
                <div class="close-link-container" @click="close">Close</div>
            </div>
        </div>
    </FilterComponent>
</template>

<script>
import { monthDayYear, iso8601Date } from '@/utils/format'
import { add as addDate, startOfYear } from '@/utils/date.js'
import FilterComponent from './FilterComponent.vue'
import { distinctBy } from '@/utils/array'

import CalendarInput from '../inputs/CalendarInput.vue'

const sections = {
    default: [
        [{ display: 'All Dates', start: () => null }],
        [
            { display: 'Last 7 Days', start: () => addDate(-7, 'days') },
            { display: 'Last 14 Days', start: () => addDate(-14, 'days') },
            { display: 'Last 30 Days', start: () => addDate(-30, 'days') },
            { display: 'Last 60 Days', start: () => addDate(-60, 'days') },
            { display: 'Last 90 Days', start: () => addDate(-90, 'days') },
            { display: 'Last 12 Months', start: () => addDate(-12, 'months') },
        ],
    ],
    short: [
        [{ display: 'All Dates', start: () => null }],
        [
            { display: 'Last Day', start: () => addDate(-1, 'days') },
            { display: 'Last 3 Days', start: () => addDate(-3, 'days') },
            { display: 'Last 7 Days', start: () => addDate(-7, 'days') },
            { display: 'Last 30 Days', start: () => addDate(-30, 'days') },
            { display: 'Last 60 Days', start: () => addDate(-60, 'days') },
            { display: 'Last 90 Days', start: () => addDate(-90, 'days') },
            { display: 'Last 12 Months', start: () => addDate(-12, 'months') },
        ],
    ],
    long: [
        [{ display: 'All Dates', start: () => null }],
        [
            { display: 'Year to Date', start: startOfYear },
            { display: 'Last 3 Months', start: () => addDate(-3, 'months') },
            { display: 'Last 6 Months', start: () => addDate(-6, 'months') },
            { display: 'Last 1 Year', start: () => addDate(-1, 'years') },
            { display: 'Last 2 Years', start: () => addDate(-2, 'years') },
            { display: 'Last 3 Years', start: () => addDate(-3, 'years') },
            { display: 'Last 5 Years', start: () => addDate(-5, 'years') },
        ],
    ],
}

export const namedDateRanges = distinctBy(Object.values(sections).flat().flat(), o => o.display)

export default {
    model: {
        prop: 'modelValue',
        event: 'update:modelValue',
    },
    props: {
        optionSet: {
            type: String,
            default: 'default',
        },
        modelValue: {
            type: Object,
            required: true,
        },
        title: {
            type: String,
            default: 'Timeline',
        },
    },
    components: {
        CalendarInput,
        FilterComponent,
    },
    data() {
        return {
            datePickerVisible: false,
            customDate: {
                startDate: null,
                endDate: null,
            },
        }
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(value) {
                if (value.isNamedRange) {
                    return
                }

                this.customDate = value
            },
        },
    },
    methods: {
        optionClicked() {
            this.close()
        },
        showDatePicker() {
            this.datePickerVisible = true
            this.$nextTick(() => {
                this.$el.querySelector('.filter-content').scrollBy(0, 10000)
            })
        },
        setDateClicked() {
            var startDate = null
            var endDate = null
            var parts = []

            if (this.customDateStart) {
                startDate = iso8601Date(this.customDateStart)
                parts.push(monthDayYear(this.customDateStart))
            } else {
                parts.push('')
            }

            if (this.customDateEnd) {
                endDate = iso8601Date(this.customDateEnd)
                parts.push(monthDayYear(this.customDateEnd))
            } else {
                parts.push('Present')
            }

            if (parts.length == 0) {
                parts = ['Custom Dates']
            }

            this.emitDateRangeChanged(startDate, endDate, parts.join(' - '), false)
            this.close()
        },
        close() {
            this.$refs.filter.close()
        },
        emitDateRangeChanged(startDate, endDate, display, isNamedRange) {
            let range = {
                startDate,
                endDate,
                display,
                isNamedRange,
            }

            this.$emit('update:modelValue', range)
        },
    },
    computed: {
        currentOption: {
            get() {
                for (let section of this.sections) {
                    for (let item of section) {
                        if (this.modelValue.display === item.display) {
                            return item
                        }
                    }
                }

                return null
            },
            set(option) {
                let startDate = option.start()
                let endDate = null

                this.emitDateRangeChanged(iso8601Date(startDate), iso8601Date(endDate), option.display, true)
                this.close()
            },
        },
        setDateButtonDisabled() {
            return !this.customDate.startDate && !this.customDate.endDate
        },
        customDateStart: {
            get() {
                return iso8601Date(this.customDate.startDate)
            },
            set(value) {
                this.customDate.startDate = iso8601Date(value)
            },
        },
        customDateEnd: {
            get() {
                return iso8601Date(this.customDate.endDate)
            },
            set(value) {
                this.customDate.endDate = iso8601Date(value)
            },
        },
        sections() {
            return sections[this.optionSet ?? 'default'] ?? sections.default
        },
    },
}
</script>
<style>
.filter-component.date-range-filter {
    .filter-content {
        min-height: 476px;
    }

    div.selection-section {
        hr {
            margin-top: 10px;
            border: none;
            border-top: 2px solid var(--color-primary-200);
        }

        input.invalid {
            border: 1px solid var(--color-error);
        }

        .selection {
            color: var(--color-primary-700);
            display: block;
            line-height: 22px;
            letter-spacing: 0.24px;
            cursor: pointer;
        }

        .custom-date-selector {
            justify-items: center;
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-top: 8px;

            span {
                display: inline-block;
                margin-left: 10px;
            }

            .boxes {
                align-items: center;
                display: flex;
                justify-content: space-between;

                input[type='date'] {
                    width: 44%;
                    padding: 8px;
                }

                span.to {
                    text-transform: uppercase;
                    text-align: center;
                    margin: 0;
                    padding: 0;
                }
            }

            .custom-date-selector-title {
                display: flex;
                align-items: center;
            }

            input:last-child {
                float: right;
            }

            button.set-date {
                height: 24px;
                line-height: 16px;
                font-size: var(--font-size-caption);
                color: var(--color-secondary-700);
                text-transform: uppercase;
                font-weight: var(--weight-black);

                border-radius: 4px;
                background-color: var(--color-primary-050);
                border: 1px solid #e5e5e5;
                box-sizing: border-box;
            }

            div.close-link-container {
                padding: 4px 0;
                text-align: center;
                text-transform: uppercase;
                font-size: var(--font-size-detail);
                font-weight: var(--weight-black);
                letter-spacing: 0.11px;
                line-height: 13px;
                color: var(--color-primary-600);
            }
        }
    }
}
</style>
